import { useEffect, useState } from "react";
import { sanitizeFilesData, optimisticFilesData } from "../assetUtils";
import { gql, useMutation, useLazyQuery } from '@apollo/client';
import difference from 'lodash/difference';
import { isJpgOrPng } from 'shared/lib/isImage';
import delay from 'shared/lib/delay';

interface UploadParams {
  path: string;
  files: File[];
  onError: (message: string) => void;
  /** The person chose to replace files with the same name; otherwise Genesis refuses them. */
  replace?: boolean;
}

interface UploadResults {
  uploading: boolean;
  progress: number; // 0 to 1
  error: string;
  // The finished files' keys (images under their compressed copy's name), once an upload is done.
  uploaded: string[] | null;
}

enum State {
  Init,
  StartSetup,
  Setup,
  StartUpload,
  Upload,
  StartPolling,
  Polling,
  StartCleanup,
  Cleanup,
  Complete,
  Reset,
  Error
}

const POLL_MAX_COUNT = 30;

export default function useUploadManager(params: UploadParams): UploadResults {
  const { path, files, onError, replace = false } = params;

  const [currentState, setCurrentState] = useState(State.Init);

  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string>();
  const [uploaded, setUploaded] = useState<string[] | null>(null);

  const [graphqlFiles, setGraphqlFiles] = useState([]);
  const [s3Files, setS3Files] = useState([]);

  const [getAssets] = useLazyQuery<{
    assets: {
      Key: string;
      Name: string;
      Size: string;
      LastModified: string;
    }[]
  }>(GET_ASSETS, {
    variables: { path },
    fetchPolicy: 'network-only'
  });
  const [uploadFiles] = useMutation(UPLOAD_FILES);
  const [
    getPresignedUrls, {
      data: presignedUrlData
    }] = useLazyQuery<{
      presignedUploadUrl: {
        Key: string;
        PresignedUrl: string;
        CreatedAt: string;
        ExpiresAt: string;
      }[]
    }>(GET_UPLOAD_PRESIGNED_URL);

  // Reset the state when the file list changes
  useEffect(() => {
    setCurrentState(State.Reset);
  }, [files]);

  // When error occurs, trigger the callback
  useEffect(() => {
    if (error && typeof onError === 'function') {
      onError(error);
    }
  }, [onError, error]);

  // Perform action based on the curent state
  useEffect(() => {
    const sanitizedFiles = sanitizeFilesData(files, path);

    const doUploadSetup = async () => {
      // Invalid files are stripped out from the list
      if (sanitizedFiles.length !== files.length || !sanitizedFiles.length) {
        const diff = difference(
          files.map(f => f.name),
          sanitizedFiles.map(f => f.file.name)
        );

        setError(`Unable to upload files: ${diff}`);
        return;
      }

      const graphqlFiles = sanitizedFiles
        .filter(f => !f.isBiggerThanSizeLimit)
        .map(f => f.file);
      const s3Files = sanitizedFiles
        .filter(f => f.isBiggerThanSizeLimit)
        .map(f => f.file);

      setGraphqlFiles(graphqlFiles);
      setS3Files(s3Files);

      setProgress(0.1);

      if (s3Files.length > 0) {
        try {
          const urls = await getPresignedUrls({
            variables: {
              // Images will still be processed even when uploaded directly
              keys: s3Files.map(f => renameImagesForConversion(f.name)),
              replace
            }
          });

          if (urls.error || urls.data?.presignedUploadUrl?.length !== s3Files.length) {
            setCurrentState(State.Error);
            setError(fileExistsMessage(urls.error) || 'Failed to retrieve enough presigned upload URLs');
            return;
          }
        } catch (e) {
          console.error(e);
          setCurrentState(State.Error);
          setError(fileExistsMessage(e) || 'Failed to retrieve presigned upload URLs');
          return;
        }
      }

      setProgress(0.33);

      setCurrentState(State.StartUpload);
    }

    const doUpload = async () => {
      if (s3Files?.length > 0) {
        const promises = s3Files.map((f: File) => {
          const url = presignedUrlData.presignedUploadUrl.find(
            // Images will still be processed even when uploaded directly
            p => p.Key.endsWith(renameImagesForConversion(f.name))
          );

          return fetch(url.PresignedUrl, {
            method: 'PUT',
            body: f
          })
        });

        try {
          await Promise.all(promises);
        } catch (e) {
          console.error(e);
          setCurrentState(State.Error);
          setError('Failed to upload large files via presigned URL');
          return;
        }
      }

      setProgress(0.5);

      // Graphql files
      if (graphqlFiles?.length > 0) {
        const optimisticFiles = optimisticFilesData(sanitizedFiles);

        try {
          await uploadFiles({
            variables: {
              files: graphqlFiles,
              path,
              size: graphqlFiles[0].size,
              replace,
            },
            optimisticResponse: {
              uploadFiles: optimisticFiles
            },
          });
        } catch (e) {
          console.error(e);
          setCurrentState(State.Error);
          setError(fileExistsMessage(e) || 'Failed to upload files via graphql');
          return;
        }
      }

      setProgress(1.0);

      setCurrentState(State.StartPolling);
    }

    const doPolling = async () => {
      // keep polling assets until conversion is done
      const imageFiles = sanitizedFiles.filter(x => isJpgOrPng(x.file.name)); // only jpg or png will be converted
      const filenamesWithConvert = imageFiles.map(x => getNameWithConvert(x.file.name));
      const filenamesWithDefault = imageFiles.map(x => getNameWithDefault(x.file.name));

      let count = 0;
      while (count <= POLL_MAX_COUNT) {
        const { data } = await getAssets();

        // check if any item of [abc_convert.jpg, cde_convert.jpg] is contained in [folder/abc_convert.jpg, folder/cde_convert.jpg, folder/1.jpg, folder/2.jpg]
        const hasSomeConvertName = data.assets.some(x => {
          return filenamesWithConvert.find(filename => x.Name.includes(filename));
        });

        // check if every item of [abc-default.jpg, cde-default.jpg] is contained in [folder/abc-default.jpg, folder/cde-default.jpg, folder/1.jpg, folder/2.jpg]
        const hasAllDefaultName = filenamesWithDefault.every(filename => {
          return data.assets.find(asset => asset.Name.includes(filename));
        });

        if (!hasSomeConvertName && hasAllDefaultName) {
          // conversion is done when assets doesn't container filename_convert.jpg and
          // all files are converted to filename-default.jpg
          setCurrentState(State.StartCleanup);
          break;
        }

        await delay(1000);
        count++;
      }

      if (count >= POLL_MAX_COUNT) {
        setCurrentState(State.Error);
        setError('Timed out while waiting for asset processing. Try refreshing the page.');
      }
    };

    switch (currentState) {
      case State.Init:
        if (files?.length > 0) {
          setCurrentState(State.StartSetup);
        }
        break;
      case State.StartSetup:
        setCurrentState(State.Setup);
        doUploadSetup();
        // go to startUpload
        break;
      case State.Setup:
        break;
      case State.StartUpload:
        setCurrentState(State.Upload);
        doUpload();
        // go to startPolling
        break;
      case State.Upload:
        break;
      case State.StartPolling:
        setCurrentState(State.Polling);
        doPolling();
        // go to startCleanup
        break;
      case State.Polling:
        break;
      case State.StartCleanup:
        setCurrentState(State.Cleanup);
        setS3Files([]);
        setGraphqlFiles([]);
        setProgress(null);
        setUploaded(sanitizedFiles.map(f => (isJpgOrPng(f.file.name) ? getNameWithDefault(f.file.name) : f.file.name)));
        setCurrentState(State.Complete);
        break;
      case State.Cleanup:
        break;
      case State.Complete:
        break;
      case State.Reset:
        setError(null);
        setUploaded(null);
        setS3Files([]);
        setGraphqlFiles([]);
        setProgress(null);
        setCurrentState(State.Init);
        break;
      case State.Error:
        setProgress(null);
        break;
      default:
        break;
    }
  }, [
    currentState,
    files,
    s3Files,
    graphqlFiles,
    path,
    getPresignedUrls,
    presignedUrlData?.presignedUploadUrl,
    uploadFiles,
    getAssets,
    replace
  ]);

  return {
    uploading: ![State.Init, State.Complete, State.Error].includes(currentState),
    progress,
    error,
    uploaded
  };
}

function renameImagesForConversion(filename) {
  return isJpgOrPng(filename) ? getNameWithConvert(filename) : filename;
}

// abc.png -> abc_convert.png
function getNameWithConvert(filename) {
  const regex = /^(.*)([.].{3,4})$/;
  const regexResult = regex.exec(filename);
  return `${regexResult[1]}_convert${regexResult[2]}`;
}

// abc.png -> abc-default.jpg
function getNameWithDefault(filename) {
  const regex = /^(.*)([.].{3,4})$/;
  const regexResult = regex.exec(filename);
  return `${regexResult[1]}-default.jpg`;
}

/** Genesis's own words when an upload would replace an existing file (FILE_EXISTS). */
function fileExistsMessage(e): string | null {
  const errors = (e && e.graphQLErrors) || [];
  const hit = errors.find(g => g && g.extensions && g.extensions.code === 'FILE_EXISTS');
  return hit ? hit.message : null;
}

const UPLOAD_FILES = gql`
  mutation uploadFiles($files: [Upload!]!, $path: String!, $size: Int!, $replace: Boolean) {
    uploadFiles(files: $files, path: $path, size: $size, replace: $replace) {
      Key
      Name: Key
      Size
      LastModified
      __typename
    }
  }
`;

const GET_UPLOAD_PRESIGNED_URL = gql`
  query presignedUploadUrl($keys: [String], $replace: Boolean) {
    presignedUploadUrl(keys: $keys, replace: $replace) {
      Key
      PresignedUrl
      CreatedAt
      ExpiresAt
      __typename
    }
  }
`;

const GET_ASSETS = gql`
  query getAssets($path: String) {
    assets (path: $path) {
      Key
      Name: Key
      Size
      LastModified
    }
  }
`;
